import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const PlayerContext = createContext(null);
const PLAY_THRESHOLD = 15; // seconds before a play counts

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.9);
  const playedRef = useRef({ id: null, secs: 0, recorded: false });
  // Guards a single fresh-url retry per song so a broken/expired signed URL
  // recovers once without looping.
  const retryRef = useRef({ song_id: null, tried: false });

  // Fetch a fresh, short-lived signed URL for a song on demand (bucket stays private).
  const fetchFreshUrl = useCallback(async (songId) => {
    if (!songId) return null;
    try {
      const { data } = await api.get(`/songs/${songId}/play-url`);
      return data?.audio_url || null;
    } catch {
      return null;
    }
  }, []);

  const playTrack = useCallback((track, list) => {
    const newQueue = list && list.length ? list : [track];
    const idx = newQueue.findIndex((t) => t.song_id === track.song_id);
    setQueue(newQueue);
    setIndex(idx < 0 ? 0 : idx);
    setCurrent(track);
    playedRef.current = { id: track.song_id, secs: 0, recorded: false };
    retryRef.current = { song_id: track.song_id, tried: false };
    setPlaying(true);
  }, []);

  // Load & play the current track. Resolves a fresh URL if the one captured at
  // page load is missing or has expired, so every card plays reliably.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    let cancelled = false;
    (async () => {
      let url = current.audio_url;
      if (!url) url = await fetchFreshUrl(current.song_id);
      if (cancelled || !el) return;
      if (!url) {
        setPlaying(false);
        toast.error('Audio indisponible pour ce morceau.');
        return;
      }
      el.src = url;
      el.volume = volume;
      if (playing) el.play().catch(() => {}); // failures handled by onError
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.song_id]);

  // Recover from a failed/expired source by fetching a fresh signed URL once.
  const onAudioError = useCallback(async () => {
    const el = audioRef.current;
    if (!el || !current) return;
    const r = retryRef.current;
    if (r.song_id === current.song_id && r.tried) {
      setPlaying(false);
      toast.error('Lecture impossible pour le moment. Réessaie.');
      return;
    }
    retryRef.current = { song_id: current.song_id, tried: true };
    const url = await fetchFreshUrl(current.song_id);
    if (url && audioRef.current) {
      audioRef.current.src = url;
      if (playing) audioRef.current.play().catch(() => setPlaying(false));
    } else {
      setPlaying(false);
      toast.error('Lecture impossible pour le moment. Réessaie.');
    }
  }, [current, playing, fetchFreshUrl]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.play().catch(() => setPlaying(false));
    else el.pause();
  }, [playing]);

  const toggle = useCallback(() => {
    if (!current) return;
    setPlaying((p) => !p);
  }, [current]);

  const next = useCallback(() => {
    if (index < queue.length - 1) {
      const t = queue[index + 1];
      setIndex(index + 1);
      setCurrent(t);
      playedRef.current = { id: t.song_id, secs: 0, recorded: false };
      retryRef.current = { song_id: t.song_id, tried: false };
      setPlaying(true);
    } else {
      setPlaying(false);
    }
  }, [index, queue]);

  const prev = useCallback(() => {
    if (index > 0) {
      const t = queue[index - 1];
      setIndex(index - 1);
      setCurrent(t);
      playedRef.current = { id: t.song_id, secs: 0, recorded: false };
      retryRef.current = { song_id: t.song_id, tried: false };
      setPlaying(true);
    }
  }, [index, queue]);

  const seek = useCallback((value) => {
    const el = audioRef.current;
    if (el && duration) {
      el.currentTime = (value / 100) * duration;
    }
  }, [duration]);

  const setVolume = useCallback((v) => {
    setVolumeState(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (!el) return;
    setProgress(el.duration ? (el.currentTime / el.duration) * 100 : 0);
    const pr = playedRef.current;
    if (current && pr.id === current.song_id) {
      pr.secs += 0.25;
      if (!pr.recorded && pr.secs >= PLAY_THRESHOLD) {
        pr.recorded = true;
        api.post('/plays', { song_id: current.song_id }).catch(() => {});
      }
    }
  };

  const value = {
    current, playing, progress, duration, volume, queue,
    playTrack, toggle, next, prev, seek, setVolume,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
        onEnded={next}
        onError={onAudioError}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);

export function fmtTime(s) {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}
